<template>
  <div class="drill-panel" @click.stop>
    <!-- Header -->
    <div class="drill-hdr">
      <div>
        <div class="drill-proc">{{ cell.processName }}</div>
        <div class="drill-period">
          {{ cell.colLabel
          }}<span v-if="cell.colSubLabel"> · {{ cell.colSubLabel }}</span>
        </div>
      </div>
      <button class="drill-close" @click="$emit('close')">✕</button>
    </div>

    <!-- Body -->
    <div class="drill-bdy">
      <div class="drill-row">
        <span>Capacity</span>
        <span class="drill-val">{{ cell.capacityHours.toFixed(2) }} hrs</span>
      </div>
      <div class="drill-row">
        <span>Demand</span>
        <span class="drill-val">{{ cell.demandHours.toFixed(2) }} hrs</span>
      </div>
      <div class="drill-row">
        <span>Utilisation</span>
        <span class="drill-val" :style="{ color: utilisationColour }"
          >{{ cell.pct }}%</span
        >
      </div>
      <div class="drill-row" v-if="cell.pct >= 100">
        <span>Over by</span>
        <span class="drill-val over">{{ overBy.toFixed(2) }} hrs</span>
      </div>
      <div class="drill-row" v-else>
        <span>Remaining</span>
        <span class="drill-val ok">{{ remaining.toFixed(2) }} hrs</span>
      </div>

      <!-- Utilisation bar -->
      <div class="drill-bar-outer">
        <div
          class="drill-bar-inner"
          :style="{ width: barWidth, background: utilisationColour }"
        ></div>
      </div>

      <div class="drill-divider"></div>

      <!-- Work orders (Day, TimelineDay, TimelineWeek, TimelineWorkWeek only) -->
      <template v-if="showWorkOrders">
        <div class="drill-wo-title">Work orders</div>
        <template v-if="filteredWorkOrders && filteredWorkOrders.length">
          <div
            class="drill-wo-item"
            v-for="(wo, i) in filteredWorkOrders"
            :key="wo.workOrderCode + '-' + i"
          >
            <div>
              <div class="drill-wo-code">{{ wo.workOrderCode }}</div>
              <div class="drill-wo-desc">{{ wo.productDescription }}</div>
            </div>
            <div class="drill-wo-hrs">{{ wo.hours.toFixed(2) }} hrs</div>
          </div>
        </template>
        <div v-else class="drill-wo-empty">No work orders scheduled.</div>
      </template>
      <div v-else class="drill-wo-empty">
        Work orders are not listed in Month view.
      </div>

      <div class="drill-divider"></div>

      <!-- Navigate button -->
      <button class="drill-go" @click="viewInSchedule">
        View in schedule →
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "CapacityDrillPanel",
  props: {
    cell: { type: Object, required: true },
    currentView: { type: String, required: true },
  },
  emits: ["close", "view-in-schedule"],

  computed: {
    showWorkOrders() {
      return this.currentView !== "TimelineMonth";
    },
    filteredWorkOrders() {
      if (!this.cell.workOrders) return [];
      return this.cell.workOrders.filter(
        (wo) => wo.workOrderCode && wo.workOrderCode !== 0 && wo.workOrderCode !== "0",
      );
    },
    utilisationColour() {
      const p = this.cell.pct;
      if (p >= 100) return "#dc2626";
      if (p >= 80) return "#d97706";
      return "#16a34a";
    },
    barWidth() {
      return `${Math.min(this.cell.pct, 100)}%`;
    },
    remaining() {
      return Math.max(
        0,
        Math.round((this.cell.capacityHours - this.cell.demandHours) * 100) / 100,
      );
    },
    overBy() {
      return (
        Math.round((this.cell.demandHours - this.cell.capacityHours) * 100) / 100
      );
    },
  },

  mounted() {
    document.addEventListener("keydown", this.onEscape);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onEscape);
  },

  methods: {
    onEscape(e) {
      if (e.key === "Escape") this.$emit("close");
    },
    viewInSchedule() {
      this.$emit("view-in-schedule", {
        processCode: this.cell.processCode,
        date: new Date(this.cell.colDates[0]),
      });
    },
  },
};
</script>

<style lang="scss">
.drill-panel {
  position: absolute;
  top: 0;
  right: 0;
  width: 252px;
  height: 100%;
  background: #fff;
  border-left: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  z-index: 20;
  box-shadow: -3px 0 10px rgba(0, 0, 0, 0.07);

  .drill-hdr {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 11px 14px;
    border-bottom: 1px solid #bae6fd;
    background: #e0f2fe;
    flex-shrink: 0;
  }
  .drill-proc {
    font-size: 13px;
    font-weight: 600;
    color: #111827;
  }
  .drill-period {
    font-size: 11px;
    color: #374151;
    margin-top: 2px;
  }
  .drill-close {
    background: none;
    border: none;
    cursor: pointer;
    color: #6b7280;
    font-size: 16px;
    padding: 0;
    line-height: 1;
  }

  .drill-bdy {
    flex: 1;
    overflow-y: auto;
    padding: 12px 14px;
  }
  .drill-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    color: #6b7280;
    margin-bottom: 6px;
  }
  .drill-val {
    font-weight: 500;
    color: #111827;
  }
  .drill-val.over {
    color: #dc2626;
  }
  .drill-val.ok {
    color: #16a34a;
  }

  .drill-bar-outer {
    height: 4px;
    background: #e5e7eb;
    margin: 6px 0 2px;
  }
  .drill-bar-inner {
    height: 100%;
    transition: width 0.3s;
  }

  .drill-divider {
    height: 1px;
    background: #e5e7eb;
    margin: 8px 0;
  }

  .drill-wo-title {
    font-size: 11px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 5px;
  }
  .drill-wo-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    font-size: 11px;
    padding: 4px 6px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    margin-bottom: 3px;
  }
  .drill-wo-code {
    font-size: 11px;
    font-weight: 500;
    color: #111827;
  }
  .drill-wo-desc {
    font-size: 10px;
    color: #9ca3af;
    margin-top: 1px;
  }
  .drill-wo-hrs {
    font-size: 11px;
    color: #6b7280;
  }
  .drill-wo-empty {
    font-size: 11px;
    color: #9ca3af;
  }

  .drill-go {
    width: 100%;
    padding: 7px;
    background: #35baf6;
    color: #fff;
    border: none;
    border-radius: 3px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    margin-top: 4px;
    &:hover {
      background: #1a9bd6;
    }
  }
}
</style>
